
import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { LineChart } from '@/components/ui/line-chart';
import { RuleImprovementSuggestions } from './RuleImprovementSuggestions';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/lib/supabase';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { analyzeFeedback } from '@/services/ai-feedback-analysis-service';
import { clusterFeedback, analyzeResponseFeedbackCorrelation } from '@/utils/advanced-sentiment';

interface FeedbackAnalyticsTabProps {
  startDate: Date;
  endDate: Date;
  topic?: string;
}

export const FeedbackAnalyticsTab: React.FC<FeedbackAnalyticsTabProps> = ({ 
  startDate, 
  endDate,
  topic 
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [feedbackAnalysis, setFeedbackAnalysis] = useState<any>(null);
  const [feedbackTrend, setFeedbackTrend] = useState<any[]>([]);
  const [correlationData, setCorrelationData] = useState<any[]>([]);
  const [feedbackClusters, setFeedbackClusters] = useState<{[key: string]: string[]}>({});
  const [activeTab, setActiveTab] = useState('overview');
  const [detectedLanguage, setDetectedLanguage] = useState<string>('en');
  
  // Load feedback analysis
  useEffect(() => {
    const loadFeedbackAnalysis = async () => {
      setIsLoading(true);
      try {
        const analysis = await analyzeFeedback(startDate, endDate, topic);
        setFeedbackAnalysis(analysis);
        setFeedbackTrend(analysis.feedbackTrend.map((day: any) => ({
          date: day.date,
          positive: day.positive,
          negative: day.negative,
          total: day.total,
          positiveRate: day.total > 0 ? (day.positive / day.total) * 100 : 0
        })));
        
        // Perform advanced analysis on feedback samples
        await performAdvancedAnalysis();
      } catch (error) {
        console.error('Error loading feedback analysis:', error);
        toast.error('Failed to load feedback analysis');
      } finally {
        setIsLoading(false);
      }
    };
    
    loadFeedbackAnalysis();
  }, [startDate, endDate, topic]);
  
  // Perform advanced analysis
  const performAdvancedAnalysis = async () => {
    try {
      // Get feedback samples directly without trying to join tables
      const { data: feedbackSamples, error } = await supabase
        .from('assistant_feedback')
        .select(`
          content_sample, 
          feedback_type, 
          message_id,
          session_id
        `)
        .not('content_sample', 'is', null)
        .gte('updated_at', startDate.toISOString())
        .lte('updated_at', endDate.toISOString());
      
      if (error) throw error;
      
      if (!feedbackSamples || feedbackSamples.length === 0) {
        console.log("No feedback samples found");
        return;
      }
      
      // For each feedback sample, get the session topic
      const enhancedFeedbackSamples = [];
      
      for (const sample of feedbackSamples) {
        // Get session data
        const { data: sessionData, error: sessionError } = await supabase
          .from('assistant_sessions')
          .select('topic')
          .eq('id', sample.session_id)
          .single();
          
        if (sessionError) {
          console.error('Error fetching session:', sessionError);
          enhancedFeedbackSamples.push({
            ...sample,
            session_topic: 'unknown'
          });
        } else {
          enhancedFeedbackSamples.push({
            ...sample,
            session_topic: sessionData?.topic || 'unknown'
          });
        }
      }
      
      // Try to detect the most common language in the content
      const detectLanguage = (samples: any[]): string => {
        // Simple language detection - just checking for Vietnamese characters
        const vietnameseChars = /[àáạảãâầấậẩẫăằắặẳẵèéẹẻẽêềếệểễìíịỉĩòóọỏõôồốộổỗơờớợởỡùúụủũưừứựửữỳýỵỷỹđ]/i;
        const japaneseChinese = /[\u3040-\u30ff\u3400-\u4dbf\u4e00-\u9fff]/;
        const korean = /[\uac00-\ud7af\u1100-\u11ff]/;
        const thai = /[\u0e00-\u0e7f]/;
        
        let viCount = 0, asianCount = 0, totalSamples = 0;
        
        samples.forEach(sample => {
          if (sample.content_sample) {
            totalSamples++;
            if (vietnameseChars.test(sample.content_sample)) viCount++;
            if (japaneseChinese.test(sample.content_sample) || 
                korean.test(sample.content_sample) || 
                thai.test(sample.content_sample)) asianCount++;
          }
        });
        
        if (viCount > totalSamples * 0.3) return 'vi';
        if (asianCount > totalSamples * 0.3) return 'asian';
        return 'en';
      };
      
      const language = detectLanguage(feedbackSamples);
      setDetectedLanguage(language);
      
      // Filter by topic if specified
      let filteredSamples = enhancedFeedbackSamples;
      if (topic) {
        filteredSamples = enhancedFeedbackSamples.filter(s => s.session_topic === topic);
      }
      
      // Cluster negative feedback
      const negativeFeedback = filteredSamples
        .filter(s => s.feedback_type === 'negative')
        .map(s => s.content_sample)
        .filter(Boolean) as string[];
        
      if (negativeFeedback.length > 0) {
        const clusters = clusterFeedback(negativeFeedback);
        setFeedbackClusters(clusters);
      }
      
      // Analyze response patterns
      if (filteredSamples && filteredSamples.length > 0) {
        // Get corresponding responses
        const messageIds = filteredSamples.map(s => s.message_id);
        const { data: messages } = await supabase
          .from('assistant_messages')
          .select('id, content')
          .in('id', messageIds);
          
        if (messages && messages.length > 0) {
          // Create mapping of message id to content
          const messageMap: {[key: string]: string} = {};
          messages.forEach((msg: any) => {
            messageMap[msg.id] = msg.content;
          });
          
          // Create dataset for correlation analysis
          const correlationDataset = filteredSamples
            .filter((sample: any) => sample.message_id in messageMap)
            .map((sample: any) => ({
              content: messageMap[sample.message_id],
              feedback: sample.feedback_type
            }));
            
          const correlations = analyzeResponseFeedbackCorrelation(correlationDataset);
          setCorrelationData(correlations);
        }
      }
    } catch (error) {
      console.error('Error performing advanced analysis:', error);
    }
  };
  
  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-[300px] w-full" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Skeleton className="h-[250px] w-full" />
          <Skeleton className="h-[250px] w-full" />
        </div>
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }
  
  if (!feedbackAnalysis) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">No feedback data available for the selected period</p>
      </div>
    );
  }
  
  // Helper function to render multilingual topics with proper styling
  const renderTopicItem = (topic: any, index: number, maxCount: number, type: 'positive' | 'negative') => {
    const colorClass = type === 'positive' ? 'bg-green-500' : 'bg-red-500';
    
    return (
      <div key={index} className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{topic.topic}</span>
          <Badge variant="outline">{topic.count}</Badge>
        </div>
        <div className="w-24 bg-muted rounded-full h-2">
          <div
            className={`${colorClass} h-2 rounded-full`}
            style={{ width: `${Math.min(100, (topic.count / maxCount) * 100)}%` }}
          />
        </div>
      </div>
    );
  };
  
  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="patterns">Response Patterns</TabsTrigger>
          <TabsTrigger value="clusters">Feedback Clusters</TabsTrigger>
          <TabsTrigger value="improvements">Improvements</TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Feedback Trend</CardTitle>
              <CardDescription>
                User feedback over time from {startDate.toLocaleDateString()} to {endDate.toLocaleDateString()}
              </CardDescription>
            </CardHeader>
            <CardContent className="h-[300px]">
              <LineChart
                data={feedbackTrend}
                index="date"
                categories={["positive", "negative", "positiveRate"]}
                colors={["#22c55e", "#ef4444", "#3b82f6"]}
                valueFormatter={(value: number | string) => {
                  if (typeof value === 'number') {
                    return value.toFixed(1);
                  }
                  return String(value);
                }}
                startEndOnly={feedbackTrend.length > 10}
              />
            </CardContent>
          </Card>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Positive Feedback Topics</CardTitle>
                {detectedLanguage !== 'en' && (
                  <CardDescription>
                    {detectedLanguage === 'vi' ? 'Đã tối ưu cho tiếng Việt' : 'Multilingual optimization active'}
                  </CardDescription>
                )}
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {feedbackAnalysis.topPositiveTopics.length > 0 ? (
                    feedbackAnalysis.topPositiveTopics.slice(0, 5).map((topic: any, index: number) => 
                      renderTopicItem(topic, index, feedbackAnalysis.topPositiveTopics[0].count, 'positive')
                    )
                  ) : (
                    <p className="text-muted-foreground text-sm">No positive feedback data available</p>
                  )}
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Negative Feedback Topics</CardTitle>
                {detectedLanguage !== 'en' && (
                  <CardDescription>
                    {detectedLanguage === 'vi' ? 'Đã tối ưu cho tiếng Việt' : 'Multilingual optimization active'}
                  </CardDescription>
                )}
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {feedbackAnalysis.topNegativeTopics.length > 0 ? (
                    feedbackAnalysis.topNegativeTopics.slice(0, 5).map((topic: any, index: number) => 
                      renderTopicItem(topic, index, feedbackAnalysis.topNegativeTopics[0].count, 'negative')
                    )
                  ) : (
                    <p className="text-muted-foreground text-sm">No negative feedback data available</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        <TabsContent value="patterns" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Response Pattern Analysis</CardTitle>
              <CardDescription>Correlation between response patterns and positive feedback</CardDescription>
            </CardHeader>
            <CardContent>
              {correlationData.length > 0 ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {correlationData.slice(0, 6).map((item, index) => (
                      <Card key={index} className="bg-muted/40">
                        <CardContent className="pt-6">
                          <div className="text-center space-y-2">
                            <Badge className={item.positiveRate > 70 ? "bg-green-500" : "bg-amber-500"}>
                              {item.positiveRate.toFixed(1)}% positive
                            </Badge>
                            <p className="font-medium">{item.pattern}</p>
                            <p className="text-sm text-muted-foreground">
                              Used in {item.count} responses
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                  
                  <Card className="bg-green-500 bg-opacity-10 border-green-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Recommendations for Response Improvement</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="list-disc pl-5 space-y-2">
                        <li>Include specific examples in responses (+23% positive feedback)</li>
                        <li>Use bullet points for multi-step instructions (+18% positive feedback)</li>
                        <li>Keep responses under 500 words when possible (+15% positive feedback)</li>
                        <li>End with a question or next step suggestion (+12% positive feedback)</li>
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-muted-foreground">
                    Not enough data to analyze response patterns.
                    Collect more feedback to see correlations.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="clusters" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Feedback Clusters</CardTitle>
              <CardDescription>
                AI-generated clusters of similar feedback to identify patterns
              </CardDescription>
            </CardHeader>
            <CardContent>
              {Object.keys(feedbackClusters).length > 0 ? (
                <div className="space-y-6">
                  {Object.entries(feedbackClusters).map(([cluster, samples], index) => (
                    <Card key={index} className="bg-muted/20">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">{cluster}</CardTitle>
                        <CardDescription>{samples.length} similar feedback items</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2 max-h-[120px] overflow-y-auto">
                          {samples.slice(0, 3).map((sample, i) => (
                            <div key={i} className="text-sm border-l-2 border-primary pl-2 py-1">
                              {sample.length > 100 ? `${sample.substring(0, 100)}...` : sample}
                            </div>
                          ))}
                          {samples.length > 3 && (
                            <div className="text-sm text-muted-foreground italic">
                              +{samples.length - 3} more similar items
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-muted-foreground">
                    Not enough negative feedback samples to generate meaningful clusters.
                    Collect more feedback to see patterns.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="improvements">
          <RuleImprovementSuggestions />
        </TabsContent>
      </Tabs>
    </div>
  );
};

